import React, { useState, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertCircle,
  Upload,
  File,
  FileText,
  Image as ImageIcon,
  Table2,
  Trash2,
  Download,
  Loader2,
  X,
  FileUp
} from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import type { IAAttestationDocument } from '@/lib/ia-calculator-utils';

interface IADocumentUploadProps {
  practiceId: number;
  attestationId: number | null;
  documents: IAAttestationDocument[];
  onUpload: (file: File, category: string) => Promise<void>;
  onDelete: (docId: number) => Promise<void>;
  onDownload: (doc: IAAttestationDocument) => Promise<void>;
  disabled?: boolean;
}

// Document categories with labels
const DOCUMENT_CATEGORIES = [
  { value: 'screenshot', label: 'Screenshot/Evidence' },
  { value: 'attestation', label: 'Attestation Form' },
  { value: 'report', label: 'Report' },
  { value: 'workflow', label: 'Workflow Documentation' },
  { value: 'patient_list', label: 'Patient List' },
  { value: 'other', label: 'Other' }
];

// File type icons
function getFileIcon(mimeType: string) {
  if (mimeType.startsWith('image/')) {
    return <ImageIcon className="h-4 w-4 text-blue-500" />;
  }
  if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) {
    return <Table2 className="h-4 w-4 text-green-500" />;
  }
  if (mimeType === 'application/pdf') {
    return <FileText className="h-4 w-4 text-red-500" />;
  }
  return <File className="h-4 w-4 text-gray-500" />;
}

// Format file size
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function IADocumentUpload({
  practiceId,
  attestationId,
  documents,
  onUpload,
  onDelete,
  onDownload,
  disabled = false
}: IADocumentUploadProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // New state for drop-first flow
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('screenshot');

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled && attestationId) {
      setIsDragging(true);
    }
  }, [disabled, attestationId]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const validateFile = (file: File): string | null => {
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'image/png',
      'image/jpeg',
      'image/jpg'
    ];

    if (!allowedTypes.includes(file.type)) {
      return 'Invalid file type. Allowed: PDF, DOCX, XLSX, PNG, JPG';
    }

    if (file.size > 10 * 1024 * 1024) {
      return 'File size must be less than 10MB';
    }

    return null;
  };

  // Called when user selects a file (via drop or click)
  const handleFileSelected = (file: File) => {
    const error = validateFile(file);
    if (error) {
      setUploadError(error);
      return;
    }

    setUploadError(null);
    setPendingFile(file);
    setSelectedCategory('screenshot'); // Reset to default
    setCategoryDialogOpen(true);
  };

  // Called when user confirms category in dialog
  const handleConfirmUpload = async () => {
    if (!pendingFile) return;
    
    setCategoryDialogOpen(false);
    setIsUploading(true);

    try {
      await onUpload(pendingFile, selectedCategory);
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Failed to upload file');
    } finally {
      setIsUploading(false);
      setPendingFile(null);
    }
  };

  // Cancel upload
  const handleCancelUpload = () => {
    setCategoryDialogOpen(false);
    setPendingFile(null);
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (disabled || !attestationId) return;

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelected(files[0]);
    }
  }, [disabled, attestationId]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelected(files[0]);
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (docId: number) => {
    setDeletingId(docId);
    try {
      await onDelete(docId);
    } finally {
      setDeletingId(null);
    }
  };

  const getCategoryLabel = (value: string) => {
    return DOCUMENT_CATEGORIES.find(c => c.value === value)?.label || value;
  };

  return (
    <div className="space-y-4">
      {/* Category Selection Dialog */}
      <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Select Document Category</DialogTitle>
            <DialogDescription>
              Choose a category for the document you're uploading.
            </DialogDescription>
          </DialogHeader>
          
          {pendingFile && (
            <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
              <FileUp className="h-5 w-5 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate">{pendingFile.name}</p>
                <p className="text-xs text-muted-foreground">
                  {(pendingFile.size / 1024).toFixed(1)} KB
                </p>
              </div>
            </div>
          )}
          
      <div className="space-y-2">
            <label className="text-sm font-medium">Category</label>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DOCUMENT_CATEGORIES.map(cat => (
                <SelectItem key={cat.value} value={cat.value}>
                  {cat.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={handleCancelUpload}>
              Cancel
            </Button>
            <Button onClick={handleConfirmUpload}>
              Upload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Area */}
      <div className="space-y-2">
        <div
          className={`
            relative border-2 border-dashed rounded-lg p-6 text-center transition-colors
            ${isDragging ? 'border-primary bg-primary/5' : 'border-gray-300 hover:border-gray-400'}
            ${disabled || !attestationId ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          `}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => !disabled && attestationId && fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
            onChange={handleFileSelect}
            disabled={disabled || !attestationId}
          />

          {isUploading ? (
            <div className="flex flex-col items-center gap-2">
              <Loader2 className="h-8 w-8 text-primary animate-spin" />
              <p className="text-sm text-gray-600">Uploading...</p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <Upload className={`h-8 w-8 ${isDragging ? 'text-primary' : 'text-gray-400'}`} />
              <p className="text-sm text-gray-600">
                {attestationId 
                  ? 'Drag and drop a file here, or click to select'
                  : 'Save attestation first to upload documents'
                }
              </p>
              <p className="text-xs text-gray-400">
                PDF, DOCX, XLSX, PNG, JPG (max 10MB)
              </p>
            </div>
          )}
        </div>
      </div>

      {uploadError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="flex items-center justify-between">
            {uploadError}
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
              onClick={() => setUploadError(null)}
            >
              <X className="h-4 w-4" />
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Document List */}
      {documents.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-gray-700">
            Uploaded Documents ({documents.length})
          </h4>
          <div className="space-y-2">
            {documents.map(doc => (
              <Card key={doc.id} className="p-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    {getFileIcon(doc.mime_type)}
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{doc.file_original_name}</p>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <span>{formatFileSize(doc.file_size)}</span>
                        <span>•</span>
                        <span>{getCategoryLabel(doc.category)}</span>
                        <span>•</span>
                        <span>{new Date(doc.uploaded_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={() => onDownload(doc)}
                      title="Download"
                    >
                      <Download className="h-4 w-4 text-gray-500" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={() => handleDelete(doc.id)}
                      disabled={deletingId === doc.id}
                      title="Delete"
                    >
                      {deletingId === doc.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4 text-red-500" />
                      )}
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default IADocumentUpload;



